import { useEffect, useMemo, useState } from "react";
import { CheckCircle, Circle, Warning, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader } from "../components/Shell";
import ExportButton from "../components/ExportButton";
import { api, periodKeyFor } from "../lib/api";
import { useStation } from "../lib/station";

const TABS = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

export default function Compliance() {
  const { activeId } = useStation();
  const [templates, setTemplates] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [tab, setTab] = useState("daily");
  const [employee, setEmployee] = useState(() => localStorage.getItem("fuelops_employee") || "");
  const [editingReminderId, setEditingReminderId] = useState(null);
  const [editingValue, setEditingValue] = useState(1);

  const refresh = async () => {
    if (!activeId) return;
    const [t, c] = await Promise.all([
      api.listChecklistTemplates(activeId),
      api.listChecklistCompletions(activeId),
    ]);
    setTemplates(t);
    setCompletions(c);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const periodKey = useMemo(() => periodKeyFor(tab, new Date()), [tab]);
  const items = templates.filter((t) => t.frequency === tab);

  const completedMap = useMemo(() => {
    const m = {};
    completions.forEach((c) => {
      if (c.period_key === periodKey) m[c.template_id] = c;
    });
    return m;
  }, [completions, periodKey]);

  const totalDoneAll = useMemo(() => {
    const map = {};
    TABS.forEach(({ key }) => {
      const pk = periodKeyFor(key, new Date());
      const tpls = templates.filter((t) => t.frequency === key);
      const done = tpls.filter((t) =>
        completions.some((c) => c.template_id === t.id && c.period_key === pk)
      ).length;
      map[key] = { done, total: tpls.length };
    });
    return map;
  }, [templates, completions]);

  if (!activeId) return null;

  const toggle = async (tpl) => {
    if (editingReminderId) return;
    if (!employee) {
      toast.error("Enter inspector name first");
      return;
    }
    const existing = completedMap[tpl.id];
    if (existing) {
      await api.uncheckChecklist(existing.id);
    } else {
      await api.completeChecklist({
        station_id: activeId,
        template_id: tpl.id,
        employee,
        period_key: periodKey,
      });
    }
    refresh();
  };

  const saveReminder = async (tpl) => {
    const val = Math.max(0, parseInt(editingValue, 10) || 0);
    await api.updateChecklistTemplate(tpl.id, { reminder_days_before: val });
    setEditingReminderId(null);
    toast.success("Reminder lead time updated");
    refresh();
  };

  return (
    <div>
      <PageHeader
        title="UST Compliance"
        subtitle="Inspection checklists"
        action={<ExportButton kind="checklist" testId="export-checklist" />}
      />

      <div className="surface rounded-lg p-3 mb-4">
        <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">Inspector</label>
        <input
          type="text"
          value={employee}
          onChange={(e) => {
            setEmployee(e.target.value);
            localStorage.setItem("fuelops_employee", e.target.value);
          }}
          placeholder="Enter your name"
          className="input-dark mt-1"
          data-testid="inspector-name"
        />
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4" data-testid="compliance-tabs">
        {TABS.map((t) => {
          const stats = totalDoneAll[t.key] || { done: 0, total: 0 };
          const allDone = stats.total > 0 && stats.done === stats.total;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`h-14 rounded-lg flex flex-col items-center justify-center transition-colors ${
                tab === t.key ? "bg-[#FF6B00] text-white" : "surface text-gray-300 hover:border-white/20"
              }`}
            >
              <span className="text-sm font-bold uppercase tracking-wider">{t.label}</span>
              <span className={`text-[10px] mt-0.5 font-mono ${tab === t.key ? "text-white/80" : allDone ? "text-[#10B981]" : "text-gray-500"}`}>
                {stats.done}/{stats.total}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-2" data-testid="checklist-items">
        {items.map((tpl) => {
          const done = !!completedMap[tpl.id];
          const editing = editingReminderId === tpl.id;
          return (
            <div
              key={tpl.id}
              className={`surface rounded-lg p-4 ${done ? "opacity-70" : "border-l-4 border-l-[#EF4444]/70"}`}
              data-testid={`compliance-item-${tpl.id}`}
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggle(tpl)}
                  data-testid={`compliance-toggle-${tpl.id}`}
                  className="flex-shrink-0"
                  aria-label="Toggle complete"
                >
                  {done ? (
                    <CheckCircle size={26} weight="fill" className="text-[#10B981]" />
                  ) : (
                    <Circle size={26} weight="bold" className="text-gray-500" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-bold ${done ? "text-gray-500 line-through" : "text-white"}`}>
                    {tpl.label}
                  </div>
                  {done ? (
                    <div className="text-[11px] text-gray-500 mt-1 font-mono">
                      ✓ {completedMap[tpl.id].employee} ·{" "}
                      {new Date(completedMap[tpl.id].completed_at).toLocaleString()}
                    </div>
                  ) : (
                    <div className="text-[11px] text-[#EF4444] mt-1 inline-flex items-center gap-1">
                      <Warning size={12} weight="fill" /> Overdue for this period
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between">
                <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500">
                  <BellRinging size={12} weight="bold" />
                  Remind
                  {editing ? (
                    <>
                      <input
                        type="number"
                        min="0"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        data-testid={`reminder-input-${tpl.id}`}
                        className="ml-1 w-14 bg-[#0F1115] border border-white/10 rounded px-2 py-0.5 text-xs text-white"
                      />
                      <span className="ml-1">day(s) before</span>
                    </>
                  ) : (
                    <span className="ml-1 font-mono text-gray-300">
                      {tpl.reminder_days_before ?? 1} day(s) before
                    </span>
                  )}
                </div>
                {editing ? (
                  <div className="flex gap-1">
                    <button
                      onClick={() => setEditingReminderId(null)}
                      data-testid={`reminder-cancel-${tpl.id}`}
                      className="h-7 px-2 text-[10px] uppercase tracking-wider text-gray-400 hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => saveReminder(tpl)}
                      data-testid={`reminder-save-${tpl.id}`}
                      className="h-7 px-2 rounded text-[10px] uppercase tracking-wider bg-[#FF6B00] text-white font-bold"
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingReminderId(tpl.id);
                      setEditingValue(tpl.reminder_days_before ?? 1);
                    }}
                    data-testid={`reminder-edit-${tpl.id}`}
                    className="text-[10px] uppercase tracking-wider text-[#FF6B00] font-bold"
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
